import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Bind a piece of state to a URL search-param key so filters / pagination
 * survive a round-trip to a child route. The component re-mounts on browser
 * back, useSearchParams() returns the restored URL, and the initial state
 * is hydrated from it. Subsequent state changes write back via REPLACE so
 * each keystroke in a search box doesn't pollute history.
 *
 * The default value is omitted from the URL — keeps it tidy when filters
 * are unchanged.
 */
export function useUrlState(key, defaultValue, opts = {}) {
  const parse = opts.parse || ((v) => v);
  const serialize = opts.serialize || ((v) => String(v));
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const [value, setValue] = useState(raw != null ? parse(raw) : defaultValue);
  const skipNextWrite = useRef(true);

  useEffect(() => {
    // Skip the initial effect — we just READ from the URL; immediately
    // writing back would be a no-op and could cancel a peer hook's write
    // happening in the same render.
    if (skipNextWrite.current) { skipNextWrite.current = false; return; }
    setParams((curr) => {
      const next = new URLSearchParams(curr);
      const isDefault = value == null || value === '' ||
        (typeof value === 'number' && Number.isNaN(value)) || value === defaultValue;
      if (isDefault) next.delete(key);
      else next.set(key, serialize(value));
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return [value, setValue];
}
